import { MarketingOptInStatusDTO } from '@hexcrawl/shared-utils';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email?: string;
  /** Address awaiting confirmation; set until the verification link is clicked. */
  pendingEmail?: string;
  isGuest: boolean;
  isAdmin?: boolean;
  hasAgeVerification?: boolean;
  oauthAccounts?: Array<{ provider: string; providerUsername?: string; providerAvatar?: string }>;
  /** Present once the player has answered the marketing prompt, whichever way they answered. */
  marketingOptIn?: MarketingOptInStatusDTO;
  /**
   * Whether to offer the opt-in prompt. Decided by the server and only recomputed when
   * `GET /auth/me` runs, so it reflects login or session restore rather than the current moment.
   */
  showMarketingPrompt?: boolean;
  /**
   * Whether to ask this guest to attach an address so their account survives. Server-decided, and
   * like `showMarketingPrompt` only recomputed when `GET /auth/me` runs.
   */
  showAccountLinkPrompt?: boolean;
  /** When the current session lapses — for a guest with no address, when the account becomes lost. */
  sessionExpiresAt?: string;
}

export interface AuthNotice {
  text: string;
  kind: 'error' | 'success';
}

interface AuthState {
  // State
  user: User | null;
  sessionToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /**
   * A message to show on the login screen after an auth callback. Survives the callback component
   * unmounting when the URL is cleaned, which is why it lives here and not in local state.
   */
  authNotice: AuthNotice | null;

  // Actions
  setUser: (user: User) => void;
  /** Merge fields into the current user, for local updates that need no round trip. */
  patchUser: (patch: Partial<User>) => void;
  setSessionToken: (token: string) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAuthNotice: (notice: AuthNotice | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      sessionToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      authNotice: null,

      setUser: (user) => set({ user, isAuthenticated: true }),
      patchUser: (patch) => set((state) => (
        state.user ? { user: { ...state.user, ...patch } } : {}
      )),
      setSessionToken: (token) => set({ sessionToken: token, isAuthenticated: true }),
      clearAuth: () => set({ 
        user: null,
        sessionToken: null, 
        isAuthenticated: false 
      }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      setAuthNotice: (authNotice) => set({ authNotice }),
    }),
    {
      name: 'hexcrawl-auth',
      partialize: (state) => ({ 
        sessionToken: state.sessionToken,
        user: state.user,
      }),
      merge: (persistedState: unknown, currentState) => {
        const incoming = (persistedState as Partial<AuthState>) || undefined;
        const merged = {
          ...currentState,
          ...(incoming || {}),
        } as AuthState;
        
        // Validate user data - if user exists but is missing required fields, clear it
        if (merged.user && !merged.user.id) {
          console.warn('Invalid user data in storage (missing id), clearing...');
          merged.user = null;
          merged.sessionToken = null;
          merged.isAuthenticated = false;
          return merged;
        }
        
        // Compute isAuthenticated from persisted values
        merged.isAuthenticated = Boolean(
          (incoming && (incoming.sessionToken || incoming.user)) ||
          merged.sessionToken ||
          merged.user
        );
        return merged;
      },
    }
  )
);
